import React from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";

import { Otp } from "../../Otp";
import routes from "../../../routes";
import { OtpChannel, VIEW_IDS } from "../../../constants";
import { StyledFormConfirm } from "./styled";
import {
  getIsEmailChanged,
  getIsMobileChanged,
  getIsMultiPartnerJourney,
  getIsSignatoryPrimaryEmailChanged,
  getIsSignatoryPrimaryMobileNoChanged
} from "../../../store/selectors/appConfig";
import { getViewIdOfSearchResultById } from "../../../store/selectors/searchProspect";
import { getSelectedStakeholder } from "../../../store/selectors/stakeholders";
import { getOtp } from "../../../store/selectors/otp";
import { checkIfEfrNotRequiredForNonSignatory } from "../../../utils/checkIfEfrNotRequiredForNonSignatory";

/**
 * Determines redirect route for signatory stakeholders
 * @param {Object} params - Parameters needed for route determination
 * @returns {string} The route to redirect to
 */
const getSignatoryRedirectRoute = ({
  isEmailChangedInPreviousStep,
  isSignatoryPrimaryEmailChanged,
  otpType,
  isVerified,
  isFromPreview
}) => {
  if (
    (isEmailChangedInPreviousStep || isSignatoryPrimaryEmailChanged) &&
    otpType === OtpChannel.Sms
  ) {
    return routes.verifyEmailOtp;
  }

  if ((otpType === OtpChannel.Email || otpType === OtpChannel.Sms) && isVerified) {
    return isFromPreview ? routes.StakeholderTermsAndConditions : routes.stakeholdersInfo;
  }

  return routes.stakeholdersInfo;
};

/**
 * Determines redirect route for non-signatory stakeholders
 * @param {Object} params - Parameters needed for route determination
 * @returns {string} The route to redirect to
 */
const getNonSignatoryRedirectRoute = ({
  isEmailChangedInPreviousStep,
  isSignatoryPrimaryEmailChanged,
  isSignatoryPrimaryMobileNoChanged,
  otpType,
  isVerified,
  isFromPreview,
  selectedPartner
}) => {
  if (
    (isEmailChangedInPreviousStep || isSignatoryPrimaryEmailChanged) &&
    otpType === OtpChannel.Sms
  ) {
    return routes.verifyEmailOtp;
  }

  const showNonSignatory = checkIfEfrNotRequiredForNonSignatory(selectedPartner);
  if (showNonSignatory) {
    return routes.additionalStakeholderInformation;
  }

  if (
    (isSignatoryPrimaryMobileNoChanged || isSignatoryPrimaryEmailChanged || isFromPreview) &&
    isVerified
  ) {
    return routes.StakeholderTermsAndConditions;
  }

  return routes.stakeholdersInfo;
};

/**
 * Determines redirect route for non-partner journey
 * @param {Object} params - Parameters needed for route determination
 * @returns {string} The route to redirect to
 */
const getNonPartnerRedirectRoute = ({
  otpType,
  isMobileChangedInPreviousStep,
  isEmailChangedInPreviousStep,
  viewId
}) => {
  if (
    otpType === OtpChannel.Sms &&
    (isMobileChangedInPreviousStep && isEmailChangedInPreviousStep)
  ) {
    return routes.verifyEmailOtp;
  }

  if (isMobileChangedInPreviousStep || isEmailChangedInPreviousStep || viewId) {
    return routes.StakeholderTermsAndConditions;
  }

  return otpType === OtpChannel.Sms ? routes.verifyEmailOtp : routes.companyInfo;
};

/**
 * Determines the appropriate redirect route based on application state
 * @param {Object} props - Component properties and state
 * @returns {string} The route to redirect to
 */
const determineRedirectRoute = ({
  otpType,
  isMultiPartnerJourney,
  selectedPartner,
  isEmailChangedInPreviousStep,
  isSignatoryPrimaryEmailChanged,
  isSignatoryPrimaryMobileNoChanged,
  isMobileChangedInPreviousStep,
  isVerified,
  isFromPreview,
  viewId
}) => {
  if (!isMultiPartnerJourney || !selectedPartner || Object.keys(selectedPartner).length === 0) {
    return getNonPartnerRedirectRoute({
      otpType,
      isMobileChangedInPreviousStep,
      isEmailChangedInPreviousStep,
      viewId
    });
  }

  const {
    kycDetails: { isSignatory }
  } = selectedPartner;

  if (isSignatory) {
    return getSignatoryRedirectRoute({
      isEmailChangedInPreviousStep,
      isSignatoryPrimaryEmailChanged,
      otpType,
      isVerified,
      isFromPreview
    });
  }

  return getNonSignatoryRedirectRoute({
    isEmailChangedInPreviousStep,
    isSignatoryPrimaryEmailChanged,
    isSignatoryPrimaryMobileNoChanged,
    otpType,
    isVerified,
    isFromPreview,
    selectedPartner
  });
};

export const FormConfirm = ({ otpType, title, info, changeText }) => {
  const location = useLocation();
  const isMobileChangedInPreviousStep = useSelector(getIsMobileChanged);
  const isEmailChangedInPreviousStep = useSelector(getIsEmailChanged);
  const isSignatoryPrimaryEmailChanged = useSelector(getIsSignatoryPrimaryEmailChanged);
  const isSignatoryPrimaryMobileNoChanged = useSelector(getIsSignatoryPrimaryMobileNoChanged);
  const isMultiPartnerJourney = useSelector(getIsMultiPartnerJourney);
  const selectedPartner = useSelector(getSelectedStakeholder);
  const { isVerified } = useSelector(getOtp);
  const viewId = useSelector(getViewIdOfSearchResultById);
  const isFromPreview =
    location?.state?.isFromPreview || selectedPartner?.viewId === VIEW_IDS.StakeholdersInfoPreview;

  const redirectTo = determineRedirectRoute({
    otpType,
    isMultiPartnerJourney,
    selectedPartner,
    isEmailChangedInPreviousStep,
    isSignatoryPrimaryEmailChanged,
    isSignatoryPrimaryMobileNoChanged,
    isMobileChangedInPreviousStep,
    isVerified,
    isFromPreview,
    viewId
  });

  return (
    <div>
      <StyledFormConfirm data-testid="otp-form-confirm-wrapper">
        <Otp
          title={title}
          info={info}
          changeText={changeText}
          otpType={otpType}
          redirectRoute={redirectTo}
          selectedPartner={selectedPartner}
          classes={{
            centeredContainer: "centeredContainer",
            title: "title"
          }}
          isFromPreview={isFromPreview}
        />
      </StyledFormConfirm>
    </div>
  );
};

FormConfirm.propTypes = {
  otpType: PropTypes.string,
  title: PropTypes.string,
  info: PropTypes.node,
  changeText: PropTypes.string
};
